/*
===============================================================================
bieloadsession.normalizers.ts
===============================================================================
*/

import {
  BieLoadSession,
  BieLoadSessionActions,
  BieLoadSessionComboItem,
  BieLoadSessionFindResult,
  BieLoadSessionRecommendedAction,
  BieLoadSessionScopeComboItem,
  BieLoadSessionStateCode,
  BieSaveResult,
  IBieLoadSessionActions,
  IBieLoadSessionReopenResult,
  IBieLoadSessionStageResult,
} from './bieloadsession.models';

function firstDefined(source: any, keys: string[]): any {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }

  return undefined;
}

function asString(value: any, fallback = ''): string {
  if (value === undefined || value === null) {
    return fallback;
  }

  return String(value);
}

function asNullableString(value: any): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return String(value);
}

function asNumber(value: any, fallback = 0): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function asNullableNumber(value: any): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function asBoolean(value: any): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function asObject(value: any): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
}

function deriveActions(source: any): BieLoadSessionActions {
  const stateCode = asString(
    firstDefined(source, ['statecode', 'stateCode', 'stc'])
  ).toUpperCase();

  const workbookAvailable = asBoolean(
    firstDefined(source, ['workbookavailable', 'workbookAvailable'])
  );

  const replaceAllowed = asBoolean(
    firstDefined(source, ['workbookreplaceallowed', 'workbookReplaceAllowed'])
  );

  const itemCount = asNumber(
    firstDefined(source, ['onboardingitemcount', 'onboardingItemCount'])
  );

  const canEditContext = stateCode === 'DRAFT';

  const canUploadWorkbook = ['DRAFT', 'STAGED', 'REJECTED'].includes(stateCode);

  const canOpenWorkbench = workbookAvailable && itemCount > 0;

  const canValidate =
    canOpenWorkbench && ['STAGED', 'REJECTED'].includes(stateCode);

  const canCancel = ['DRAFT', 'STAGED', 'REJECTED'].includes(stateCode);

  const canReopen = ['CANCELLED', 'REJECTED'].includes(stateCode);

  const readOnly = [
    'VALIDATED',
    'PUBLISHED',
    'CANCELLED',
    'ROLLEDBACK',
  ].includes(stateCode);

  return {
    canEditContext,
    canUploadWorkbook,
    canReplaceWorkbook: replaceAllowed,

    canOpenWorkbench,
    canValidate,
    canCancel,
    canReopen,
    readOnly,
  };
}

export function normalizeBieSaveResult(source: any): BieSaveResult {
  const rid = asNumber(firstDefined(source, ['rid', 'recid', 'loadSessionId']));

  const stp = asString(firstDefined(source, ['stp', 'stamp']));

  if (rid <= 0) {
    throw new Error('The Load Session operation did not return a valid rid.');
  }

  if (!stp) {
    throw new Error(
      'The Load Session operation did not return a concurrency stamp.'
    );
  }

  return {
    rid,
    stp,
  };
}

export function normalizeBieLoadSession(source: any): BieLoadSession {
  const stateCode = asString(
    firstDefined(source, ['statecode', 'stateCode', 'stc'])
  ).toUpperCase();

  return {
    rid: asNumber(firstDefined(source, ['recid', 'rid'])),

    rix: asString(firstDefined(source, ['recix', 'rix'])),

    name: asString(firstDefined(source, ['recname', 'name', 'nam'])),

    shortcode: asString(firstDefined(source, ['shortcode', 'shc', 'code'])),

    description: asNullableString(firstDefined(source, ['description', 'dsc'])),

    scopeId: asNumber(firstDefined(source, ['scopeid', 'scopeId', 'sci'])),

    scopeCode: asString(
      firstDefined(source, ['scopecode', 'scopeCode', 'scc'])
    ).toUpperCase(),

    scopeName: asString(
      firstDefined(source, ['scopename', 'scopeName', 'scn'])
    ),

    stateId: asNumber(firstDefined(source, ['stateid', 'stateId', 'sti'])),

    stateCode,

    stateName: asString(
      firstDefined(source, ['statename', 'stateName', 'stn'])
    ),

    sourceOrganization: asString(
      firstDefined(source, ['sourceorganization', 'sourceOrganization', 'sco'])
    ),

    sourceSystem: asString(
      firstDefined(source, ['sourcesystem', 'sourceSystem', 'ssy'])
    ),

    coverageStartDate: asNullableString(
      firstDefined(source, ['coveragestartdate', 'coverageStartDate', 'csd'])
    ),

    coverageEndDate: asNullableString(
      firstDefined(source, ['coverageenddate', 'coverageEndDate', 'ced'])
    ),

    historicalCutoffDate: asNullableString(
      firstDefined(source, [
        'historicalcutoffdate',
        'historicalCutoffDate',
        'hcd',
      ])
    ),

    observedCoverageStartDate: asNullableString(
      firstDefined(source, [
        'observedcoveragestartdate',
        'observedCoverageStartDate',
      ])
    ),

    observedCoverageEndDate: asNullableString(
      firstDefined(source, [
        'observedcoverageenddate',
        'observedCoverageEndDate',
      ])
    ),

    historicalCoverageStatus: asString(
      firstDefined(source, [
        'historicalcoveragestatus',
        'historicalCoverageStatus',
      ])
    ),

    historicalCoverageLabel: asString(
      firstDefined(source, [
        'historicalcoveragelabel',
        'historicalCoverageLabel',
      ])
    ),

    creatorId: asNullableNumber(
      firstDefined(source, ['creatorid', 'creatorId'])
    ),

    validatorId: asNullableNumber(
      firstDefined(source, ['validatorid', 'validatorId'])
    ),

    validatedAt: asNullableString(
      firstDefined(source, ['validatedat', 'validatedAt'])
    ),

    publisherId: asNullableNumber(
      firstDefined(source, ['publisherid', 'publisherId'])
    ),

    publishedAt: asNullableString(
      firstDefined(source, ['publishedat', 'publishedAt'])
    ),

    cancellerId: asNullableNumber(
      firstDefined(source, ['cancellerid', 'cancellerId'])
    ),

    cancelledAt: asNullableString(
      firstDefined(source, ['cancelledat', 'cancelledAt'])
    ),

    created: asNullableString(firstDefined(source, ['created'])),

    extraInfo: asObject(
      firstDefined(source, ['extrainfo', 'extraInfo', 'exi'])
    ),

    status: asNumber(firstDefined(source, ['status', 'sts']), 1),

    stamp: asString(firstDefined(source, ['stamp', 'stp'])),

    statistics: {
      importCount: asNumber(source?.importcount),

      historicalFactCount: asNumber(source?.historicalfactcount),

      forecastBatchCount: asNumber(source?.forecastbatchcount),

      onboardingImportCount: asNumber(source?.onboardingimportcount),

      onboardingItemCount: asNumber(source?.onboardingitemcount),

      pendingCount: asNumber(source?.onboardingpendingcount),

      validCount: asNumber(source?.onboardingvalidcount),

      warningCount: asNumber(source?.onboardingwarningcount),

      failedCount: asNumber(source?.onboardingfailedcount),

      resolvedRecordClassCount: asNumber(source?.resolvedrecordclasscount),

      unresolvedRecordClassCount: asNumber(source?.unresolvedrecordclasscount),

      issueCount: asNumber(source?.onboardingissuecount),

      informationIssueCount: asNumber(source?.onboardinginfoissuecount),

      warningIssueCount: asNumber(source?.onboardingwarningissuecount),

      errorIssueCount: asNumber(source?.onboardingerrorissuecount),

      blockingIssueCount: asNumber(source?.onboardingblockingissuecount),

      unresolvedReferenceCount: asNumber(
        source?.onboardingunresolvedreferencecount
      ),

      validationStatus: asString(source?.onboardingvalidationstatus),

      validationReady: asBoolean(source?.onboardingvalidationready),

      importClassSummary: source?.importclasssummary ?? [],
    },

    workbook: {
      available: asBoolean(source?.workbookavailable),

      status: asString(source?.workbookstatus, 'NOT_UPLOADED'),

      uploadRequired: asBoolean(source?.workbookuploadrequired),

      replaceAllowed: asBoolean(source?.workbookreplaceallowed),

      latestImportId: asNullableNumber(source?.latestimportid),

      latestImportIx: asNullableString(source?.latestimportix),

      latestImportTrackId: asNullableString(source?.latestimporttrackid),

      sourceId: asNullableNumber(source?.workbooksourceid),

      sourceCode: asNullableString(source?.workbooksourcecode),

      sourceName: asNullableString(source?.workbooksourcename),

      latestImportClassId: asNullableNumber(source?.latestimportclassid),

      latestImportClassCode: asNullableString(source?.latestimportclasscode),

      latestImportClassName: asNullableString(source?.latestimportclassname),

      latestImportStateId: asNullableNumber(source?.latestimportstateid),

      latestImportStateCode: asNullableString(source?.latestimportstatecode),

      latestImportStateName: asNullableString(source?.latestimportstatename),

      latestSheetName: asNullableString(source?.latestsheetname),

      filename: asNullableString(source?.workbookfilename),

      version: asNullableString(source?.workbookversion),

      checksum: asNullableString(source?.workbookchecksum),

      uploaderId: asNullableNumber(source?.workbookuploaderid),

      uploaderIx: asNullableString(source?.workbookuploaderix),

      uploaderCode: asNullableString(source?.workbookuploadercode),

      uploaderName: asNullableString(source?.workbookuploadername),

      uploaderEmail: asNullableString(source?.workbookuploaderemail),

      uploadedAt: asNullableString(source?.workbookuploadedat),

      createdAt: asNullableString(source?.workbookcreatedat),

      latestImportStamp: asNullableString(source?.latestimportstamp),
    },

    actions: deriveActions(source),

    totalCount:
      source?.totalcount === undefined
        ? undefined
        : asNumber(source.totalcount),

    raw: source,
  };
}

export function normalizeBieLoadSessionFindResult(
  source: any[],
  pageOffset: number,
  pageLimit: number
): BieLoadSessionFindResult {
  const normalizedRows = Array.isArray(source)
    ? source.map(normalizeBieLoadSession)
    : [];

  const totalCount =
    normalizedRows.length > 0
      ? (normalizedRows[0].totalCount ?? normalizedRows.length)
      : 0;

  return {
    rows: normalizedRows,

    totalCount,

    pageOffset,

    pageLimit,
  };
}

export function normalizeBieLoadSessionComboItem(
  source: any
): BieLoadSessionComboItem {
  return {
    rid: asNumber(firstDefined(source, ['rid', 'recid'])),

    rix: asString(firstDefined(source, ['rix', 'recix'])),

    shortcode: asString(firstDefined(source, ['shc', 'shortcode'])),

    name: asString(firstDefined(source, ['nam', 'recname'])),

    stateCode: asString(firstDefined(source, ['stc', 'statecode'])),

    stateName: asString(firstDefined(source, ['stn', 'statename'])),

    scopeId: asNumber(firstDefined(source, ['sci', 'scopeid', 'scopeId'])),

    scopeCode: asString(
      firstDefined(source, ['scc', 'scopecode', 'scopeCode'])
    ).toUpperCase(),

    scopeName: asString(
      firstDefined(source, ['scn', 'scopename', 'scopeName'])
    ),

    sourceSystem: asString(
      firstDefined(source, ['ssy', 'sourcesystem', 'sourceSystem'])
    ),

    coverageStartDate: asNullableString(
      firstDefined(source, ['csd', 'coveragestartdate', 'coverageStartDate'])
    ),

    coverageEndDate: asNullableString(
      firstDefined(source, ['ced', 'coverageenddate', 'coverageEndDate'])
    ),

    stamp: asString(firstDefined(source, ['stp', 'stamp'])),

    raw: source,
  };
}

export function normalizeBieLoadSessionScopeComboItem(
  source: any
): BieLoadSessionScopeComboItem {
  return {
    rid: asNumber(firstDefined(source, ['rid', 'recid'])),

    rix: asString(firstDefined(source, ['rix', 'recix'])),

    nam: asString(firstDefined(source, ['nam', 'recname'])),

    shc: asString(firstDefined(source, ['shc', 'shortcode'])),

    dsc: asString(firstDefined(source, ['dsc', 'description'])),

    stp: asString(firstDefined(source, ['stp', 'stamp'])),

    raw: source,
  };
}

export function normalizeBieLoadSessionStageResult(
  source: any
): IBieLoadSessionStageResult {
  const data = source?.sd ?? source ?? {};

  return {
    rid: Number(data.rid ?? data.loadSessionId ?? 0),

    state: String(data.state ?? 'STAGED') as BieLoadSessionStateCode,

    importCount: Number(data.importCount ?? data.importcount ?? 0),

    stamp: String(data.stamp ?? data.stp ?? ''),
  };
}

export function normalizeBieLoadSessionReopenResult(
  source: any
): IBieLoadSessionReopenResult {
  const data = source?.sd ?? source ?? {};

  return {
    rid: Number(data.rid ?? data.loadSessionId ?? 0),

    state: String(data.state ?? 'STAGED') as BieLoadSessionStateCode,

    stamp: String(data.stamp ?? data.stp ?? ''),
  };
}

export function normalizeBieLoadSessionActions(
  source: any
): IBieLoadSessionActions {
  const data = source?.sd ?? source ?? {};

  return {
    stateCode: String(data.stateCode ?? data.statecode ?? '').toUpperCase(),

    canReopenForCorrection: Boolean(
      data.canReopenForCorrection ?? data.canreopenforcorrection ?? false
    ),

    canUploadWorkbook: Boolean(
      data.canUploadWorkbook ?? data.canuploadworkbook ?? false
    ),

    canReplaceWorkbook: Boolean(
      data.canReplaceWorkbook ?? data.canreplaceworkbook ?? false
    ),

    canStage: Boolean(data.canStage ?? data.canstage ?? false),

    canValidate: Boolean(data.canValidate ?? data.canvalidate ?? false),

    workbookCount: Number(data.workbookCount ?? data.workbookcount ?? 0),

    itemCount: Number(data.itemCount ?? data.itemcount ?? 0),

    recommendedAction: String(
      data.recommendedAction ?? data.recommendedaction ?? 'NONE'
    ).toUpperCase() as BieLoadSessionRecommendedAction,
  };
}
